import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import { useFormikContext } from 'formik';
import { type ReactElement, useContext, useEffect } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { ProjectFormikType, RequiredProjectFieldsInfo } from '@core/formik/project';
import type { ProjectStatus } from '@core/types/api/ProjectStatus/ProjectStatus';
import type { User } from '@core/types/api/User/User';
import { hasCompetitorLossDataFromFormik } from '@core/utils/competitors';
import { FormatDateString } from '@core/utils/dateHelpers';
import { hasValue } from '@core/utils/equality';
import { getCurrencySymbol } from '@core/utils/format';
import {
  getIsShowCompetitorInfoSectionFromProjectFormik,
  isArchiveProjectStatus,
  isLostProjectStatus,
} from '@core/utils/projectHelpers';
import { requiredLabel } from '@core/utils/strings';
import {
  ApiLookupInput,
  InputDropdown,
  LoadingSpinner,
  LocalLookupInput,
  type LookupInputOption,
} from '@web/components';
import { Input } from '@web/components/Input/Input';
import { LossReasonSection } from '@web/components/LossReasonSection/LossReasonSection';
import { useSalespersonList } from '@web/hooks/useSalespersonList';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import { lookups } from '@web/utils/DomainHelpers';

export const ProjectInformationSection = ({
  focusField,
  requiredFields,
}: {
  focusField?: string;
  requiredFields: RequiredProjectFieldsInfo;
}): ReactElement => {
  const ctx = useContext(SlabContext);
  const usesProjectNumberOverride = ctx.userInfo.tenant.useProjectNumberAutogeneration;
  const formikBag = useFormikContext<ProjectFormikType>();

  const { data: lossReasons } = useSlabQuery('GET project loss reasons', {});

  // Competitor price inputs use the currency of the project's first plant.
  const firstPlantID = formikBag.values.productsByPlants?.[0]?.plant?.id ?? null;
  const { data: maybeFirstPlant } = useSlabQuery(
    'GET plant by ID',
    { pathParams: { id: firstPlantID ?? '' } },
    { enabled: firstPlantID !== null },
  );
  const competitorCurrencySymbol = getCurrencySymbol(
    firstPlantID === null ? null : maybeFirstPlant?.currencyCode,
  );

  const {
    isLoading: isLoadingSalespeople,
    isError: isErrorSalespeople,
    data: salespersonList,
  } = useSalespersonList();

  const {
    isLoading: isLoadingStatuses,
    isError: isErrorStatuses,
    data: statusList,
  } = useSlabQuery('GET project statuses', {});

  const isLoading = isLoadingSalespeople || isLoadingStatuses;

  const currentStatus =
    statusList?.items.find((s) => s.id === formikBag.values.projectStatus.id) ?? null;

  const showCompetitorInfoSection = getIsShowCompetitorInfoSectionFromProjectFormik(
    statusList?.items,
    formikBag.values,
  );

  useEffect(() => {
    if (isLoadingStatuses) return;
    formikBag.setFieldValue('projectStatus.systemBehavior', currentStatus?.systemBehavior ?? null);
  }, [formikBag.values.projectStatus.id, isLoadingStatuses]);

  // Clear loss-related fields when the user picks a status that is neither lost
  // nor archived. Tied to the status dropdown's selection so it only fires on an
  // actual user change, not on (re)mount with already-loaded data.
  const handleStatusChange = (newStatusId: string): void => {
    const newStatus = statusList?.items.find((s) => s.id === newStatusId) ?? null;
    if (
      newStatus !== null &&
      (isLostProjectStatus(newStatus) || isArchiveProjectStatus(newStatus))
    ) {
      return;
    }
    if (!hasCompetitorLossDataFromFormik(formikBag.values)) {
      return;
    }
    formikBag.setFieldValue('lossReason', { id: null, option: null });
    formikBag.setFieldValue('lossReasonNotes', null);
    formikBag.setFieldValue('competitor', { id: null, option: null });
    formikBag.setFieldValue('competitorProducts', []);
  };

  useEffect((): (() => void) | undefined => {
    if (!hasValue(focusField) || isLoading) {
      return undefined;
    }

    const timer = setTimeout(() => {
      const field = document.querySelector(`[name="${focusField}"]`) as HTMLInputElement;
      if (hasValue(field)) {
        field.focus();
        if (
          (focusField === 'estimatedStartDate' || focusField === 'estimatedEndDate') &&
          field.type === 'date'
        ) {
          try {
            if ('showPicker' in field && typeof field.showPicker === 'function') {
              field.showPicker();
            } else {
              field.click();
              field.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
              field.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
            }
          } catch {
            field.click();
          }
        }
      }
    }, 200);
    return (): void => clearTimeout(timer);
  }, [focusField, isLoading]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const isError = isErrorSalespeople || isErrorStatuses;
  const isDataUndefined = salespersonList === undefined || statusList === undefined;

  if (isError || isDataUndefined) {
    return <div>ERROR</div>;
  }

  const userLookups = lookups({
    items: salespersonList.items,
    label: (u: User) => u.fullName(),
    count: salespersonList.items.length,
  });

  const statusLookups = lookups({
    ...statusList,
    label: (p: ProjectStatus) => p.name,
    compare: null,
  });

  const winningCompanyLookups: LookupInputOption[] = formikBag.values.contractors
    .map((c) => {
      if ('option' in c.company) {
        return c.company.option;
      }
      return null;
    })
    .flatMap((opt) => (opt !== null ? [opt] : []));

  return (
    <Box display='flex' flexDirection='column' gap='1rem'>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Input name='name' label='Project Name*' />
        </Grid>
        <Grid item xs={6}>
          <Input
            name='projectNumber'
            label='Project Number'
            disabled={usesProjectNumberOverride}
            tip={
              usesProjectNumberOverride ? 'The project number will be generated on save' : undefined
            }
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Input name='projectJobNumber' label='Project Job Number' />
        </Grid>
        <Grid item xs={4}>
          <InputDropdown name='user.id' label='Salesperson*' options={userLookups} />
        </Grid>
        <Grid item xs={4}>
          <Input name='owner' label='Owner' />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InputDropdown
            name='projectStatus.id'
            label='Status*'
            options={statusLookups}
            onSelect={async (newStatusId): Promise<void> => handleStatusChange(newStatusId)}
          />
        </Grid>
        <Grid item xs={6}>
          <LocalLookupInput
            formState={formikBag.values}
            name='winningCompany.company'
            label='Project winner'
            options={winningCompanyLookups}
          />
        </Grid>
      </Grid>
      {showCompetitorInfoSection && (
        <LossReasonSection
          isLossReasonRequired={requiredFields.lossDetails}
          isCompetitorRequired={requiredFields.lossDetails}
          title='Why was this project lost?'
          lossReasons={lossReasons?.items ?? []}
          currencySymbol={competitorCurrencySymbol}
        />
      )}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Input
            label={requiredLabel(`Est. Start Date`, requiredFields.estimatedStartDate)}
            name='estimatedStartDate'
            type='date'
            max={
              formikBag.values.estimatedEndDate !== null
                ? FormatDateString(formikBag.values.estimatedEndDate)
                : undefined
            }
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            label={requiredLabel(`Est. End Date`, requiredFields.estimatedEndDate)}
            name='estimatedEndDate'
            type='date'
            min={
              formikBag.values.estimatedStartDate !== null
                ? FormatDateString(formikBag.values.estimatedStartDate)
                : undefined
            }
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Input
            label={requiredLabel(`Bid Date`, requiredFields.bidDate)}
            name='bidDate'
            type='date'
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            label={requiredLabel(`Expiration Date`, requiredFields.expirationDate)}
            name='expirationDate'
            type='date'
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Input label='Confidence %' name='confidence' type='number' />
        </Grid>
        <Grid item xs={6}>
          <ApiLookupInput
            formState={formikBag.values}
            label={requiredLabel(`Tax Code`, requiredFields.taxCode)}
            name='taxCode'
            route={{
              barrel: 'GET tax codes',
              args: (inputText, { constructQueryParams }) => constructQueryParams(inputText),
              transform: (taxCodeList): LookupInputOption[] =>
                lookups({
                  ...taxCodeList,
                  items: taxCodeList.items,
                  label: (t) => t.name,
                  sublabels: (t) => [t.code],
                }),
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <InputDropdown
            name='isPubliclyFunded'
            label='Is this project publicly funded?'
            options={[
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ]}
          />
        </Grid>
        <Grid item xs={6}>
          <ApiLookupInput
            formState={formikBag.values}
            label={requiredLabel('Segment', requiredFields.segment)}
            name='segment'
            route={{
              barrel: 'GET segments',
              args: (inputText, { constructQueryParams }) => constructQueryParams(inputText),
              transform: (segmentList): LookupInputOption[] =>
                lookups({
                  ...segmentList,
                  items: segmentList.items,
                  label: (t) => t.name,
                  sublabels: (t) => (t.alternateID ? [t.alternateID] : []),
                }),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
